import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../firebase/AuthContext';
import { FaTrophy, FaMedal, FaCrown, FaSpinner } from 'react-icons/fa';

const LeaderboardPage = () => {
  const { currentUser } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = {};
      
      usersSnapshot.forEach(doc => {
        usersData[doc.id] = {
          id: doc.id,
          displayName: doc.data().displayName || 'Anonymous Collector',
          email: doc.data().email,
          totalSpent: 0,
          rewardPoints: 0
        };
      });

      // Get all orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      
      ordersSnapshot.forEach(doc => {
        const order = doc.data();
        const userId = order.userId;
        
        if (!userId || !usersData[userId]) return;
        
        // Only count paid orders
        const isPaid = order.paymentStatus === 'paid' || 
                       order.status === 'completed' || 
                       order.status === 'shipped' ||
                       order.status === 'processing';
        
        if (!isPaid) return;
        
        const orderTotal = order.orderDetails?.total || order.total || 0;
        usersData[userId].totalSpent += orderTotal;
      });

      // Get all treasure hunt plays and add to total spent
      const treasureHuntSnapshot = await getDocs(collection(db, 'treasureHuntPlays'));
      
      treasureHuntSnapshot.forEach(doc => {
        const play = doc.data();
        const userId = play.userId;
        
        if (!userId || !usersData[userId]) return;
        
        const playAmount = play.pricePaid || 0;
        usersData[userId].totalSpent += playAmount;
      });

      // Calculate reward points and sort
      const leaderboardData = Object.values(usersData)
        .map(user => ({
          ...user,
          rewardPoints: Math.floor(user.totalSpent / 10)
        }))
        .filter(user => user.rewardPoints > 0)
        .sort((a, b) => b.rewardPoints - a.rewardPoints)
        .slice(0, 50); // Top 50

      setLeaderboard(leaderboardData);

      // Find current user's rank
      if (currentUser) {
        const allUsers = Object.values(usersData)
          .map(user => ({
            ...user,
            rewardPoints: Math.floor(user.totalSpent / 10)
          }))
          .sort((a, b) => b.rewardPoints - a.rewardPoints);
        
        const rank = allUsers.findIndex(u => u.id === currentUser.uid) + 1;
        const userData = allUsers.find(u => u.id === currentUser.uid);
        
        if (rank > 0 && userData) {
          setUserRank({
            rank,
            ...userData
          });
        }
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <FaCrown style={{ color: '#FFD700' }} />;
      case 2:
        return <FaMedal style={{ color: '#C0C0C0' }} />;
      case 3:
        return <FaMedal style={{ color: '#CD7F32' }} />;
      default:
        return <span>#{rank}</span>;
    }
  };

  const maskEmail = (email) => {
    if (!email) return 'Anonymous';
    const [name, domain] = email.split('@');
    if (name.length <= 2) return email;
    return `${name.substring(0, 2)}***@${domain}`;
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <FaSpinner className="spin" />
          <p>Loading leaderboard...</p>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>
            <FaTrophy /> Rewards Leaderboard
          </PageTitle>
          <PageSubtitle>
            Top collectors ranked by reward points • 1 point per $10 spent on orders & treasure hunt
          </PageSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        {/* User's Rank Card */}
        {userRank && (
          <UserRankCard>
            <UserRankTitle>Your Ranking</UserRankTitle>
            <UserRankContent>
              <RankBadge $isTop3={userRank.rank <= 3}>
                {getRankIcon(userRank.rank)}
                {userRank.rank > 3 && <span>#{userRank.rank}</span>}
              </RankBadge>
              <UserRankDetails>
                <UserRankName>{currentUser?.displayName || 'You'}</UserRankName>
                <UserRankPoints>{userRank.rewardPoints.toLocaleString()} points</UserRankPoints>
              </UserRankDetails>
            </UserRankContent>
          </UserRankCard>
        )}

        {/* Leaderboard Table */}
        <LeaderboardCard>
          <LeaderboardTitle>Top 50 Collectors</LeaderboardTitle>
          {leaderboard.length > 0 ? (
            <LeaderboardTable>
              <thead>
                <tr>
                  <Th>Rank</Th>
                  <Th>Collector</Th>
                  <Th>Points</Th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((user, index) => (
                  <LeaderboardRow 
                    key={user.id} 
                    $isCurrentUser={currentUser?.uid === user.id}
                    $rank={index + 1}
                  >
                    <Td>
                      <RankCell $rank={index + 1}>
                        {getRankIcon(index + 1)}
                      </RankCell>
                    </Td>
                    <Td>
                      <CollectorName $isCurrentUser={currentUser?.uid === user.id}>
                        {user.displayName || maskEmail(user.email)}
                        {currentUser?.uid === user.id && <YouBadge>You</YouBadge>}
                      </CollectorName>
                    </Td>
                    <Td>
                      <PointsCell>{user.rewardPoints.toLocaleString()}</PointsCell>
                    </Td>
                  </LeaderboardRow>
                ))}
              </tbody>
            </LeaderboardTable>
          ) : (
            <EmptyState>
              <p>No collectors on the leaderboard yet.</p>
              <p>Be the first to earn reward points!</p>
            </EmptyState>
          )}
        </LeaderboardCard>

        {/* How It Works */}
        <InfoCard>
          <InfoTitle>How Reward Points Work</InfoTitle>
          <InfoContent>
            <InfoItem>
              <InfoIcon>💰</InfoIcon>
              <InfoText>
                <strong>Earn Points</strong>
                <span>Get 1 reward point for every $10 you spend on orders and treasure hunt plays</span>
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoIcon>📦</InfoIcon>
              <InfoText>
                <strong>Qualifying Orders</strong>
                <span>Points are awarded for completed, shipped, or processing orders</span>
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoIcon>🏆</InfoIcon>
              <InfoText>
                <strong>Climb the Ranks</strong>
                <span>Shop more to increase your points and climb the leaderboard</span>
              </InfoText>
            </InfoItem>
          </InfoContent>
        </InfoCard>
      </ContentSection>
    </PageContainer>
  );
};

// Styled Components
const spin = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  ${spin}
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #D4AF37;
  
  .spin {
    animation: spin 1s linear infinite;
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 50%, #D4AF37 100%);
  padding: 3rem 2rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #333;
`;

const ContentSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const UserRankCard = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(212, 175, 55, 0.1));
  border: 2px solid #D4AF37;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const UserRankTitle = styled.h3`
  color: #D4AF37;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const UserRankContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const RankBadge = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$isTop3 ? 'linear-gradient(135deg, #D4AF37, #F4D03F)' : 'rgba(255,255,255,0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${props => props.$isTop3 ? '#1a1a2e' : '#D4AF37'};
  font-weight: bold;
`;

const UserRankDetails = styled.div`
  flex: 1;
`;

const UserRankName = styled.div`
  color: white;
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
`;

const UserRankPoints = styled.div`
  color: #D4AF37;
  font-size: 1.1rem;
`;

const LeaderboardCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(212, 175, 55, 0.3);
`;

const LeaderboardTitle = styled.h3`
  color: #D4AF37;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const LeaderboardTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  color: #aaa;
  font-weight: 500;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const LeaderboardRow = styled.tr`
  background: ${props => {
    if (props.$isCurrentUser) return 'rgba(212, 175, 55, 0.15)';
    if (props.$rank === 1) return 'rgba(255, 215, 0, 0.1)';
    if (props.$rank === 2) return 'rgba(192, 192, 192, 0.1)';
    if (props.$rank === 3) return 'rgba(205, 127, 50, 0.1)';
    return 'transparent';
  }};
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const RankCell = styled.div`
  font-size: ${props => props.$rank <= 3 ? '1.5rem' : '1rem'};
  color: ${props => {
    if (props.$rank === 1) return '#FFD700';
    if (props.$rank === 2) return '#C0C0C0';
    if (props.$rank === 3) return '#CD7F32';
    return '#aaa';
  }};
  font-weight: bold;
`;

const CollectorName = styled.div`
  color: ${props => props.$isCurrentUser ? '#D4AF37' : 'white'};
  font-weight: ${props => props.$isCurrentUser ? 'bold' : 'normal'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const YouBadge = styled.span`
  background: #D4AF37;
  color: #1a1a2e;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-weight: bold;
`;

const PointsCell = styled.div`
  color: #D4AF37;
  font-weight: bold;
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #aaa;
  
  p {
    margin: 0.5rem 0;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(212, 175, 55, 0.3);
`;

const InfoTitle = styled.h3`
  color: #D4AF37;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
  width: 40px;
  text-align: center;
`;

const InfoText = styled.div`
  flex: 1;
  
  strong {
    display: block;
    color: white;
    margin-bottom: 0.25rem;
  }
  
  span {
    color: #aaa;
    font-size: 0.9rem;
  }
`;

export default LeaderboardPage;
